/**
 * Full-viewport image lightbox (web), Gemini's image expansion dialog: the
 * lightbox colour at 90% with a 2px blur behind, a transparent surface, the
 * image centred with `object-fit: contain` (never cropped, never scrolled),
 * and round 40px actions: Download full size, Copy link, Close. Top right on
 * wide screens, a bottom row under 600px. `ImageLightbox.tsx` is the native
 * twin with the same props.
 *
 * Editing (plan 7.3, 7.5): Edit opens a prompt field ("Describe the change,
 * for example: make the background white"); while the edit runs the image
 * shows a pending overlay, and the new version replaces it when it settles
 * (a failed edit says why). The version control steps through the image's
 * versions, with Restore on an older one.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { SpinnerGlyph } from '../common/SpinnerGlyph';
import { useToast } from '../../hooks/useToast';
import { useArtifactSignedUrl } from '../../hooks/useArtifact';
import { downloadArtifactFile } from '../../lib/artifact-download';
import { copyArtifactLink } from '../../lib/artifact-links';
import type { ImageLightboxProps } from './artifact-model';
import { useEditingAvailable } from '../../hooks/useArtifactVersions';
import { useImageEditController } from './editor/useImageEditController';
import { useArtifactEditFor, useSettleArtifactEdits } from './artifact-edit-activity';
import { useVersionControl } from './versions/useVersionControl';
import { OlderVersionBanner, VersionControl } from './versions/VersionControl.web';

export function ImageLightbox({ artifactId, title, onClose }: ImageLightboxProps) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const [viewingId, setViewingId] = useState<string | null>(null);
  // An agent edit of this image (`editImage` in the chat) shows here too:
  // pending while it runs, then the new version (user test fix).
  const agentEdit = useArtifactEditFor(artifactId);
  const edit = useImageEditController(artifactId, { followVersionId: agentEdit?.versionId ?? null });
  const head = edit.detail.data?.currentVersion ?? null;
  const editing = edit.running || agentEdit?.phase === 'running';
  useSettleArtifactEdits(
    artifactId,
    head,
    agentEdit?.versionId && edit.failure ? agentEdit.versionId : null,
  );
  const signed = useArtifactSignedUrl(artifactId, 'image', true, 'light', {
    versionId: viewingId,
    revision: head?.id ?? null,
  });
  const editingAvailable = useEditingAvailable();
  const versions = useVersionControl({ artifactId, viewingId, onViewingChange: setViewingId });
  const [prompt, setPrompt] = useState<string | null>(null);
  const promptRef = useRef<HTMLInputElement | null>(null);
  const canEdit = editingAvailable && versions.model.isLatest && edit.detail.data?.status === 'ready';
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (typeof document !== 'undefined') setTarget(document.body);
  }, []);

  // Focus moves in on open; the trigger takes it back on close (the caller).
  useEffect(() => {
    if (!target) return;
    const frame = window.requestAnimationFrame(() => closeRef.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(frame);
  }, [target]);

  if (!target) return null;

  const download = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await downloadArtifactFile({ artifactId, variant: 'image', title });
    } catch {
      toast.error(t('chat.artifacts.download.failed'));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (await copyArtifactLink(artifactId)) toast.success(t('chat.artifacts.actions.linkCopied'));
    else toast.error(t('chat.artifacts.actions.copyFailed'));
  };

  const submitEdit = async (event: FormEvent) => {
    event.preventDefault();
    if (!prompt?.trim() || editing) return;
    if (await edit.submit(prompt)) setPrompt(null);
  };

  const actions = (
    <>
      <VersionControl control={versions} compact />
      {canEdit ? (
        <button
          type="button"
          className="tabbio-lightbox-button"
          onClick={() => {
            edit.reset();
            setPrompt((value) => (value === null ? '' : null));
          }}
          aria-label={t('chat.artifacts.image.editLabel')}
          title={t('chat.artifacts.image.editLabel')}
          aria-expanded={prompt !== null}
          disabled={editing}
        >
          <MaterialSymbol name="edit" size={20} />
        </button>
      ) : null}
      <button
        type="button"
        className="tabbio-lightbox-button"
        onClick={() => void download()}
        aria-label={t('chat.artifacts.actions.downloadFullSize')}
        title={t('chat.artifacts.actions.downloadFullSize')}
        aria-busy={busy || undefined}
      >
        {busy ? (
          <SpinnerGlyph color="var(--text-secondary)" size={20} />
        ) : (
          <MaterialSymbol name="download" size={20} />
        )}
      </button>
      <button
        type="button"
        className="tabbio-lightbox-button"
        onClick={() => void copyLink()}
        aria-label={t('chat.artifacts.actions.copyLink')}
        title={t('chat.artifacts.actions.copyLink')}
      >
        <MaterialSymbol name="link" size={20} />
      </button>
      <button
        ref={closeRef}
        type="button"
        className="tabbio-lightbox-button"
        onClick={onClose}
        aria-label={t('chat.artifacts.actions.close')}
        title={t('chat.artifacts.actions.close')}
      >
        <MaterialSymbol name="close" size={20} />
      </button>
    </>
  );

  return createPortal(
    <div
      className="tabbio-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="tabbio-lightbox-backdrop" onClick={onClose} />
      <div className="tabbio-lightbox-actions">{actions}</div>
      <div
        className="tabbio-lightbox-stage"
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        {signed.url && !signed.expired ? (
          <span className="tabbio-lightbox-figure">
            <img
              key={signed.url}
              src={signed.url}
              alt={title}
              className={`tabbio-lightbox-image${editing ? ' is-editing' : ''}`}
              referrerPolicy="no-referrer"
              onError={signed.reportLoadError}
              draggable={false}
            />
          </span>
        ) : (
          <SpinnerGlyph color="var(--text-secondary)" size={28} />
        )}
        {editing ? (
          <span className="tabbio-image-edit-pill" role="status">
            <SpinnerGlyph color="var(--bg)" size={18} />
            {t('chat.artifacts.image.editing')}
          </span>
        ) : null}
      </div>
      <div className="tabbio-lightbox-footer">
        <OlderVersionBanner
          control={versions}
          onRestored={(number) => toast.success(t('chat.artifacts.versions.restored', { number }))}
          onRestoreFailed={() => toast.error(t('chat.artifacts.versions.restoreFailed'))}
        />
        {edit.failure ? (
          <div className="tabbio-image-edit-error" role="alert">
            <MaterialSymbol name="error" size={18} />
            <span dir="auto">{edit.failure.message || t('chat.artifacts.image.editFailed')}</span>
          </div>
        ) : null}
        {prompt !== null && canEdit ? (
          <form className="tabbio-image-edit-form" onSubmit={(event) => void submitEdit(event)}>
            <label className="tabbio-visually-hidden" htmlFor="tabbio-image-edit-prompt">
              {t('chat.artifacts.image.editPrompt')}
            </label>
            <input
              id="tabbio-image-edit-prompt"
              ref={promptRef}
              dir="auto"
              autoFocus
              maxLength={2000}
              value={prompt}
              placeholder={t('chat.artifacts.image.editPlaceholder')}
              onChange={(event) => setPrompt(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.stopPropagation();
                  setPrompt(null);
                }
              }}
            />
            <button type="submit" className="tabbio-panel-primary is-small" disabled={!prompt.trim() || editing}>
              {t('chat.artifacts.image.editSubmit')}
            </button>
          </form>
        ) : null}
      </div>
    </div>,
    target,
  );
}
